import { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { ReceiveInvoiceDialog } from "../components/shopping/ReceiveInvoiceDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Loader2,
  ShoppingCart,
  ClipboardList,
  MoreVertical,
  Check,
  X,
  Truck,
  Trash2,
  Package,
  ArrowRight,
  Calendar,
  Plus,
  Eye,
  Printer,
  MapPin,
  Sparkles,
  Store,
  ListOrdered,
  TrendingDown,
  TrendingUp,
  RefreshCw,
  Pencil,
  ChevronUp,
  ChevronDown,
  Save,
  FileText,
  Upload,
  AlertCircle,
  CheckCircle2,
  Mail,
  Send,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatCurrency, formatDate, formatDateTime } from "../lib/utils";
import SupermarketSelector from "../components/shopping-lists/SupermarketSelector";
import ViewListDialog from "../components/shopping-lists/ViewListDialog";
import ConvertToOrderDialog from "../components/shopping-lists/ConvertToOrderDialog";
import { applyPriceMultiplier, getSavingsLabel, findBestMatch } from "../components/shopping-lists/helpers";
import { PRICE_MULTIPLIERS } from "../components/shopping-lists/constants";
import { generatePrintHTML, openPrintWindow } from "../components/shopping-lists/print-utils";

export default function ShoppingListsPage() {
  const { currentRestaurant } = useOutletContext();
  const [lists, setLists] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  // Detail / print
  const [viewingList, setViewingList] = useState(null);

  // Supermarkets
  const [supermarkets, setSupermarkets] = useState([]);
  const [loadingSupermarkets, setLoadingSupermarkets] = useState(false);
  const [selectedSupermarket, setSelectedSupermarket] = useState(null);
  const [supermarketError, setSupermarketError] = useState("");
  const [supermarketSort, setSupermarketSort] = useState("distance"); // "distance" | "price"
  const [userCoords, setUserCoords] = useState(null);
  const [showMap, setShowMap] = useState(false);

  // AI organized list – keyed by list_id so each card is independent
  const [organizingList, setOrganizingList] = useState(null);
  const [organizedDataMap, setOrganizedDataMap] = useState({}); // { listId: result }

  // Track previous supermarket to detect changes
  const prevSupermarketRef = useRef(null);

  // Receive dialog state
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivingList, setReceivingList] = useState(null);
  const [receiveIngredients, setReceiveIngredients] = useState([]);
  const [receiveMappings, setReceiveMappings] = useState({});
  const [receiveSelectedItems, setReceiveSelectedItems] = useState([]);
  const [savingReceive, setSavingReceive] = useState(false);
  const [receiveProgress, setReceiveProgress] = useState("");
  const [loadingReceive, setLoadingReceive] = useState(false);

  // Inline edit mode
  const [editingListId, setEditingListId] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editSelected, setEditSelected] = useState(new Set());
  const [savingEdit, setSavingEdit] = useState(false);

  // Invoice receive mode
  const [receiveInvoiceDialogOpen, setReceiveInvoiceDialogOpen] = useState(false);
  const [receiveInvoiceLoading, setReceiveInvoiceLoading] = useState(false);
  const [invoiceComparison, setInvoiceComparison] = useState(null);
  const [confirmingInvoice, setConfirmingInvoice] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const receiveInvoiceFileRef = useRef(null);

  // Send email
  const [emailInputListId, setEmailInputListId] = useState(null);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [listsData, suppliersData] = await Promise.all([
        api.getShoppingLists(currentRestaurant.restaurant_id),
        api.getSuppliers(currentRestaurant.restaurant_id)
      ]);
      setLists(listsData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant]);

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
  }, [currentRestaurant, fetchData]);

  // Auto re-organize when supermarket CHANGES (not on initial pick)
  useEffect(() => {
    const prev = prevSupermarketRef.current;
    prevSupermarketRef.current = selectedSupermarket;

    // Only trigger when switching from one supermarket to another
    if (!prev || !selectedSupermarket || prev.id === selectedSupermarket.id) return;

    const organizedListIds = Object.keys(organizedDataMap);
    if (organizedListIds.length === 0) return;

    // Re-run organize for each already-organized list with the new supermarket
    organizedListIds.forEach((listId) => {
      const list = lists.find((l) => l.list_id === listId);
      if (!list) return;
      const reOrganize = async () => {
        setOrganizingList(listId);
        try {
          const result = await api.organizeShoppingListByAisles(
            listId,
            selectedSupermarket.name,
            selectedSupermarket.brand_key || ""
          );
          setOrganizedDataMap((prev) => ({ ...prev, [listId]: result }));
        } catch (err) {
          toast.error("Error al actualizar pasillos");
        } finally {
          setOrganizingList(null);
        }
      };
      reOrganize();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupermarket]);

  // ── Print ──────────────────────────────────────────────────────────
  const handlePrint = (list, withAisles = false) => {
    const currentOrganized = organizedDataMap[list.list_id];
    const html = generatePrintHTML(list, {
      withAisles,
      organizedData: currentOrganized,
      selectedSupermarket
    });
    openPrintWindow(html);
  };
  const findBestMatchLocal = (itemName, ingList) => {
    return findBestMatch(itemName, ingList);
  };

  // ── PDF download & preview ───────────────────────────────────────
  const handleDownloadPdf = async (listId) => {
    const organized = organizedDataMap[listId];
    if (!organized) return;
    setDownloadingPdf(true);
    try {
      const { blob, filename } = await api.downloadShoppingListPdf(listId, organized);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (err) {
      toast.error(err?.message || "Error al generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreviewPdf = async (listId) => {
    const organized = organizedDataMap[listId];
    if (!organized) return;
    setDownloadingPdf(true);
    try {
      const { blob } = await api.downloadShoppingListPdf(listId, organized);
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, "_blank");
      // Revoke after a delay so the tab has time to load the blob
      if (tab) setTimeout(() => URL.revokeObjectURL(url), 30000);
      else toast.error("Activa los popups para previsualizar el PDF");
    } catch (err) {
      toast.error(err?.message || "Error al generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── Email send ────────────────────────────────────────────────────
  const handleSendEmail = async (listId) => {
    const email = emailInputValue.trim();
    if (!email || !email.includes("@")) {
      toast.error("Introduce un email válido");
      return;
    }
    setSendingEmail(true);
    try {
      // Pass organized data if the list is currently in "by aisles" view
      const organized = organizedDataMap[listId] || null;
      await api.sendShoppingListEmail(listId, email, organized);
      toast.success(`Lista enviada a ${email}`);
      setEmailInputListId(null);
      setEmailInputValue("");
    } catch (err) {
      toast.error(err?.message || "Error al enviar el email");
    } finally {
      setSendingEmail(false);
    }
  };
  const normStr = (s) =>
    (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9 ]/g, "").trim();

  const findBestInvoiceMatch = (listItemName, invoiceItems) => {
    const targetWords = normStr(listItemName).split(/\s+/).filter(w => w.length > 2);
    if (!targetWords.length) return -1;
    let bestScore = 0, bestIdx = -1;
    invoiceItems.forEach((inv, idx) => {
      const invWords = normStr(inv.name || "").split(/\s+/).filter(w => w.length > 2);
      const common = targetWords.filter(tw => invWords.some(iw => iw.includes(tw) || tw.includes(iw)));
      const score = common.length / Math.max(targetWords.length, invWords.length, 1);
      if (score > bestScore && score >= 0.25) { bestScore = score; bestIdx = idx; }
    });
    return bestIdx;
  };

  const openReceiveInvoiceDialog = async (list) => {
    setReceivingList(list);
    setInvoiceComparison(null);
    setReceiveInvoiceLoading(false);
    setConfirmingInvoice(false);
    setInvoiceProgress("");
    setInvoiceFile(null);
    setInvoicePreview(null);
    setReceiveInvoiceDialogOpen(true);
    // Pre-load ingredients for matching
    try {
      const ings = await api.getIngredients(currentRestaurant.restaurant_id);
      setReceiveIngredients(ings);
    } catch {}
  };

  const handleInvoiceFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInvoiceFile(file);
    if (file.type.startsWith("image/")) {
      setInvoicePreview(URL.createObjectURL(file));
    } else {
      setInvoicePreview(null);
    }
    handleInvoiceFileForReceive(file);
  };

  const handleInvoiceFileForReceive = async (file) => {
    if (!file || !receivingList) return;
    setReceiveInvoiceLoading(true);
    setInvoiceComparison(null);
    try {
      const result = await api.aiExtractInvoice(file, currentRestaurant?.restaurant_id);
      if (!result.success || !result.data?.items?.length) {
        toast.error("No se pudieron extraer productos de la factura");
        return;
      }
      const remaining = [...result.data.items];
      const matched = [];
      const unmatched = [];
      for (const item of receivingList.items) {
        const idx = findBestInvoiceMatch(item.ingredient_name, remaining);
        if (idx >= 0) {
          const inv = remaining[idx];
          const receivedQty = inv.quantity != null ? Math.min(inv.quantity, item.quantity) : item.quantity;
          matched.push({
            list_item: item,
            invoice_item: inv,
            received_qty: receivedQty,
            pending_qty: Math.max(0, item.quantity - receivedQty),
          });
          remaining.splice(idx, 1);
        } else {
          unmatched.push({ ...item });
        }
      }
      setInvoiceComparison({
        supplier_name: result.data.supplier_name,
        invoice_date: result.data.invoice_date,
        total: result.data.total,
        matched,
        unmatched,
      });
      toast.success(`Factura procesada: ${result.data.items.length} productos encontrados`);
    } catch {
      toast.error("Error al procesar la factura. Comprueba que la imagen sea legible.");
    } finally {
      setReceiveInvoiceLoading(false);
      if (receiveInvoiceFileRef.current) receiveInvoiceFileRef.current.value = "";
    }
  };

  const updateInvoiceMatchedQty = (idx, rawVal) => {
    const qty = Math.max(0, parseFloat(rawVal) || 0);
    setInvoiceComparison(prev => ({
      ...prev,
      matched: prev.matched.map((m, i) => i !== idx ? m : {
        ...m,
        received_qty: qty,
        pending_qty: Math.max(0, m.list_item.quantity - qty),
      }),
    }));
  };

  const confirmInvoiceReceive = async () => {
    if (!invoiceComparison || !receivingList) return;
    setConfirmingInvoice(true);
    try {
      let updated = 0, created = 0;
      setInvoiceProgress("Actualizando inventario...");
      for (const match of invoiceComparison.matched) {
        if (match.received_qty <= 0) continue;
        const item = match.list_item;
        const costPerUnit = match.invoice_item.unit_price
          || (item.quantity > 0 ? item.estimated_cost / item.quantity : 0);
        try {
          const existing = receiveIngredients.find(ing => {
            const n = normStr(ing.name), t = normStr(item.ingredient_name);
            return n === t || n.includes(t) || t.includes(n);
          });
          if (existing) {
            await api.updateIngredient(existing.ingredient_id, {
              name: existing.name,
              unit: existing.unit,
              cost_per_unit: costPerUnit || existing.cost_per_unit,
              waste_percentage: existing.waste_percentage,
              supplier_id: existing.supplier_id || "",
              min_stock: existing.min_stock,
              current_stock: existing.current_stock + match.received_qty,
            });
            updated++;
          } else {
            await api.createIngredient(currentRestaurant.restaurant_id, {
              name: item.ingredient_name,
              unit: item.unit || "kg",
              cost_per_unit: costPerUnit,
              waste_percentage: 10,
              current_stock: match.received_qty,
              min_stock: 0,
            });
            created++;
          }
        } catch (e) {
          console.error("Error updating ingredient", item.ingredient_name, e);
        }
      }

      // Build pending items
      const pendingItems = [
        ...invoiceComparison.unmatched.map(item => ({
          ingredient_name: item.ingredient_name,
          quantity: item.quantity,
          unit: item.unit,
          estimated_cost: item.estimated_cost,
        })),
        ...invoiceComparison.matched
          .filter(m => m.pending_qty > 0)
          .map(m => ({
            ingredient_name: m.list_item.ingredient_name,
            quantity: m.pending_qty,
            unit: m.list_item.unit,
            estimated_cost: m.list_item.quantity > 0
              ? Math.round(m.list_item.estimated_cost * (m.pending_qty / m.list_item.quantity) * 100) / 100
              : 0,
          })),
      ];

      if (pendingItems.length > 0) {
        setInvoiceProgress(`Creando lista de pendientes (${pendingItems.length} productos)...`);
        await api.createShoppingList(currentRestaurant.restaurant_id, {
          name: `${receivingList.name} — Pendientes`,
          items: pendingItems,
          notes: `Productos no comprados de "${receivingList.name}"`,
          list_type: "shopping",
        });
      }

      await api.updateShoppingListStatus(receivingList.list_id, "received", false);
      const updatedLists = await api.getShoppingLists(currentRestaurant.restaurant_id);
      setLists(updatedLists);

      setReceiveInvoiceDialogOpen(false);
      setInvoiceComparison(null);
      setReceivingList(null);
      toast.success(
        `Inventario: ${updated} actualizados, ${created} nuevos.` +
        (pendingItems.length > 0 ? ` Lista "${receivingList.name} — Pendientes" creada con ${pendingItems.length} productos.` : "")
      );
    } catch {
      toast.error("Error al confirmar la recepción");
    } finally {
      setConfirmingInvoice(false);
      setInvoiceProgress("");
    }
  };

  const openReceiveDialog = async (list) => {
    setReceivingList(list);
    setReceiveDialogOpen(true);
    setLoadingReceive(true);
    try {
      const ings = await api.getIngredients(currentRestaurant.restaurant_id);
      setReceiveIngredients(ings);
      const allIdx = list.items.map((_, i) => i);
      setReceiveSelectedItems(allIdx);
      const mappings = {};
      list.items.forEach((item, i) => {
        const match = findBestMatch(item.ingredient_name, ings);
        mappings[i] = match
          ? { mode: 'existing', ingredientId: match.ingredient_id }
          : { mode: 'new', ingredientId: null };
      });
      setReceiveMappings(mappings);
    } catch {
      toast.error("Error al cargar ingredientes");
    } finally {
      setLoadingReceive(false);
    }
  };

  const handleReceiveConfirm = async () => {
    if (!receivingList || receiveSelectedItems.length === 0) return;
    setSavingReceive(true);

    // Collect new items needing AI waste
    const newItems = receiveSelectedItems
      .map(i => ({ index: i, item: receivingList.items[i] }))
      .filter(({ index }) => receiveMappings[index]?.mode !== 'existing');

    const wasteMap = {};
    if (newItems.length > 0) {
      setReceiveProgress(`Calculando merma con IA para ${newItems.length} nuevos ingredientes...`);
      await Promise.all(
        newItems.map(async ({ index, item }) => {
          try {
            const r = await api.aiEstimateWaste(item.ingredient_name, "", currentRestaurant?.restaurant_id);
            wasteMap[index] = r.waste_percentage;
          } catch { wasteMap[index] = 10; }
        })
      );
    }

    setReceiveProgress("Actualizando inventario...");
    let updated = 0, created = 0;

    for (const index of receiveSelectedItems) {
      const item = receivingList.items[index];
      const mapping = receiveMappings[index];
      try {
        if (mapping?.mode === 'existing' && mapping.ingredientId) {
          const existing = receiveIngredients.find(i => i.ingredient_id === mapping.ingredientId);
          if (existing) {
            await api.updateIngredient(existing.ingredient_id, {
              name: existing.name,
              unit: existing.unit,
              cost_per_unit: existing.cost_per_unit,
              waste_percentage: existing.waste_percentage,
              supplier_id: existing.supplier_id || "",
              min_stock: existing.min_stock,
              current_stock: existing.current_stock + (item.quantity || 0),
            });
            updated++;
          }
        } else {
          await api.createIngredient(currentRestaurant.restaurant_id, {
            name: item.ingredient_name,
            unit: item.unit || "kg",
            cost_per_unit: item.estimated_cost && item.quantity
              ? item.estimated_cost / item.quantity
              : 0,
            waste_percentage: wasteMap[index] ?? 10,
            current_stock: item.quantity || 0,
            min_stock: 0,
          });
          created++;
        }
      } catch (err) {
        console.error("Error processing item", item.ingredient_name, err);
      }
    }

    // Mark list as received (no backend stock update, we handled it above)
    try {
      await api.updateShoppingListStatus(receivingList.list_id, "received", false);
      setLists(prev => prev.map(l =>
        l.list_id === receivingList.list_id ? { ...l, status: "received" } : l
      ));
    } catch {}

    setSavingReceive(false);
    setReceiveProgress("");
    setReceiveDialogOpen(false);
    setReceivingList(null);
    toast.success(`Inventario actualizado: ${updated} ingredientes actualizados, ${created} nuevos creados`);
  };

  // ── Find nearby supermarkets ──────────────────────────────────────
  const findNearbySupermarkets = () => {
    setSupermarketError("");
    setLoadingSupermarkets(true);
    setSupermarkets([]);
    if (!navigator.geolocation) {
      setSupermarketError("Tu navegador no soporta geolocalización");
      setLoadingSupermarkets(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          setUserCoords({ lat: latitude, lon: longitude });
          const result = await api.getNearbySupermarkets(latitude, longitude, 3000);
          if (result.supermarkets?.length > 0) {
            setSupermarkets(result.supermarkets);
            toast.success(`${result.supermarkets.length} supermercados encontrados`);
          } else {
            setSupermarketError("No se encontraron supermercados en un radio de 3 km.");
          }
        } catch (err) {
          setSupermarketError("Error al buscar supermercados cercanos.");
        } finally {
          setLoadingSupermarkets(false);
        }
      },
      (err) => {
        setSupermarketError("No se pudo obtener tu ubicación. Permite el acceso a la geolocalización.");
        setLoadingSupermarkets(false);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  // ── AI organize list by aisles ────────────────────────────────────
  const handleOrganizeByAisles = async (list) => {
    setOrganizingList(list.list_id);
    try {
      const supermarketName = selectedSupermarket?.name || "";
      const brandKey = selectedSupermarket?.brand_key || "";
      const result = await api.organizeShoppingListByAisles(list.list_id, supermarketName, brandKey);
      setOrganizedDataMap(prev => ({ ...prev, [list.list_id]: result }));
      toast.success(`Lista organizada por pasillos${supermarketName ? ` de ${supermarketName}` : ""}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Error al organizar la lista";
      toast.error(detail, { duration: 6000 });
    } finally {
      setOrganizingList(null);
    }
  };

  const clearOrganized = (listId) =>
    setOrganizedDataMap(prev => { const n = { ...prev }; delete n[listId]; return n; });

  // ── Inline edit mode ──────────────────────────────────────────────
  const startEditing = (list) => {
    if (organizedDataMap[list.list_id]) clearOrganized(list.list_id);
    setEditItems(list.items.map(item => ({ ...item, _origCostPerUnit: item.quantity > 0 ? item.estimated_cost / item.quantity : 0 })));
    setEditSelected(new Set());
    setEditingListId(list.list_id);
  };

  const cancelEditing = () => {
    setEditingListId(null);
    setEditItems([]);
    setEditSelected(new Set());
  };

  const handleQtyChange = (idx, rawVal) => {
    const qty = parseFloat(rawVal);
    if (isNaN(qty) || qty < 0) return;
    setEditItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const cpu = item._origCostPerUnit ?? 0;
      return { ...item, quantity: qty, estimated_cost: Math.round(cpu * qty * 100) / 100 };
    }));
  };

  const handleToggleEditItem = (idx) => {
    setEditSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleSelectAllEdit = () => {
    if (editSelected.size === editItems.length) setEditSelected(new Set());
    else setEditSelected(new Set(editItems.map((_, i) => i)));
  };

  const handleRound = (direction) => {
    setEditItems(prev => prev.map((item, i) => {
      if (!editSelected.has(i)) return item;
      const newQty = direction === 'up' ? Math.ceil(item.quantity) : Math.floor(Math.max(item.quantity, 0));
      const cpu = item._origCostPerUnit ?? 0;
      return { ...item, quantity: newQty, estimated_cost: Math.round(cpu * newQty * 100) / 100 };
    }));
  };

  const saveEditing = async (listId) => {
    setSavingEdit(true);
    try {
      // Strip internal helper field before saving
      const items = editItems.map(({ _origCostPerUnit, ...item }) => item);
      const result = await api.updateShoppingListItems(listId, items);
      setLists(prev => prev.map(l =>
        l.list_id === listId ? { ...l, items, total_estimated_cost: result.total_estimated_cost } : l
      ));
      cancelEditing();
      toast.success("Lista actualizada");
    } catch {
      toast.error("Error al guardar los cambios");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleUpdateStatus = async (listId, status, updateStock = false) => {
    try {
      const result = await api.updateShoppingListStatus(listId, status, updateStock);
      setLists(lists.map(l => 
        l.list_id === listId ? { ...l, status } : l
      ));
      if (updateStock && result.stock_updated > 0) {
        toast.success(`Lista recibida. ${result.stock_updated} ingredientes actualizados en inventario`);
      } else {
        toast.success(`Lista ${status === "completed" ? "completada" : "actualizada"}`);
      }
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async (listId) => {
    if (!window.confirm("¿Eliminar esta lista?")) return;
    
    try {
      await api.deleteShoppingList(listId);
      setLists(lists.filter(l => l.list_id !== listId));
      toast.success("Lista eliminada");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openConvertDialog = (list) => {
    setSelectedList(list);
    setSelectedSupplier("");
    setConvertDialogOpen(true);
  };

  const handleConvertToOrder = async () => {
    if (!selectedSupplier) {
      toast.error("Selecciona un proveedor");
      return;
    }

    setConverting(true);
    try {
      await api.convertListToOrder(selectedList.list_id, selectedSupplier);
      setLists(lists.map(l => 
        l.list_id === selectedList.list_id ? { ...l, status: "converted" } : l
      ));
      setConvertDialogOpen(false);
      toast.success("Lista convertida a pedido");
    } catch (error) {
      toast.error("Error al convertir");
    } finally {
      setConverting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      received: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
      converted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      cancelled: "bg-muted text-muted-foreground dark:bg-gray-800 dark:text-gray-400"
    };
    const labels = {
      pending: "Pendiente",
      completed: "Completada",
      received: "Recibida",
      converted: "Convertida a pedido",
      cancelled: "Cancelada"
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getListTypeIcon = (type) => {
    return type === "order" ? Truck : ShoppingCart;
  };

  const filteredLists = lists.filter(l => {
    if (activeTab === "pending") return l.status === "pending";
    if (activeTab === "completed") return ["completed", "converted", "received"].includes(l.status);
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shopping-lists-page">
      {/* Header */}
      <PageHeader
        icon={<ClipboardList className="w-5 h-5" />}
        title="Listas de Compra"
        description="Gestiona tus listas de compra generadas desde previsiones."
        breadcrumb={[{ label: "Operaciones" }, { label: "Listas de compra" }]}
      />

      {/* Supermarket Finder */}
      <SupermarketSelector
        supermarkets={supermarkets}
        loadingSupermarkets={loadingSupermarkets}
        supermarketError={supermarketError}
        selectedSupermarket={selectedSupermarket}
        setSelectedSupermarket={setSelectedSupermarket}
        supermarketSort={supermarketSort}
        setSupermarketSort={setSupermarketSort}
        showMap={showMap}
        setShowMap={setShowMap}
        userCoords={userCoords}
        findNearbySupermarkets={findNearbySupermarkets}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <ShoppingCart className="w-4 h-4" />
            Pendientes
            {lists.filter(l => l.status === "pending").length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {lists.filter(l => l.status === "pending").length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <Check className="w-4 h-4" />
            Completadas
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            Todas
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredLists.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-16 text-center">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-heading text-lg font-semibold mb-2">Sin listas</h3>
                <p className="text-muted-foreground mb-6">
                  {activeTab === "pending" 
                    ? "No tienes listas de compra pendientes. Genera una desde Previsiones IA."
                    : "No hay listas en esta categoría."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredLists.map((list) => {
                const ListIcon = getListTypeIcon(list.list_type);
                return (
                  <Card 
                    key={list.list_id} 
                    className="border-none shadow-soft"
                    data-testid={`list-${list.list_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                            <ListIcon className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="font-heading text-lg">{list.name}</CardTitle>
                            <CardDescription className="flex items-center gap-2">
                              <Calendar className="w-3 h-3" />
                              {formatDateTime(list.created_at)}
                              {list.forecast_period && (
                                <span className="text-xs bg-muted px-2 py-0.5 rounded">
                                  {list.forecast_period}
                                </span>
                              )}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(list.status)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {list.status === "pending" && (
                                <>
                                  <DropdownMenuItem onClick={() => handleUpdateStatus(list.list_id, "completed")}>
                                    <Check className="w-4 h-4 mr-2" />
                                    Marcar completada
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => openReceiveDialog(list)}
                                    data-testid={`receive-stock-btn-${list.list_id}`}
                                  >
                                    <Package className="w-4 h-4 mr-2" />
                                    Recibida + Actualizar stock
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openReceiveInvoiceDialog(list)}
                                    data-testid={`receive-invoice-btn-${list.list_id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Recibir con factura/ticket
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openConvertDialog(list)}>
                                    <Truck className="w-4 h-4 mr-2" />
                                    Convertir a pedido
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem 
                                onClick={() => handleDelete(list.list_id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {organizedDataMap[list.list_id] ? (
                        /* ── Inline organized-by-aisles view ── */
                        <div className="space-y-2">
                          {/* Header bar */}
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Store className="w-3.5 h-3.5 text-primary" />
                              {organizedDataMap[list.list_id].supermarket ? (
                                <>Pasillos de <strong className="text-foreground">{organizedDataMap[list.list_id].supermarket}</strong></>
                              ) : (
                                "Organizado por pasillos"
                              )}
                              <span className="text-muted-foreground/60">·</span>
                              <span>{organizedDataMap[list.list_id].total_items} productos</span>
                              {selectedSupermarket && organizingList !== list.list_id && (() => {
                                const tier = selectedSupermarket.price_tier;
                                const mult = PRICE_MULTIPLIERS[tier] ?? 1;
                                const pct = Math.round(Math.abs(1 - mult) * 100);
                                if (mult < 1) return (
                                  <span className="flex items-center gap-0.5 text-emerald-700 font-medium ml-1">
                                    <TrendingDown className="w-3 h-3" />-{pct}% vs precio medio
                                  </span>
                                );
                                if (mult > 1) return (
                                  <span className="flex items-center gap-0.5 text-amber-700 font-medium ml-1">
                                    <TrendingUp className="w-3 h-3" />+{pct}% vs precio medio
                                  </span>
                                );
                                return null;
                              })()}
                              {organizingList === list.list_id && (
                                <span className="flex items-center gap-1 text-primary ml-1">
                                  <RefreshCw className="w-3 h-3 animate-spin" />Actualizando...
                                </span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs gap-1 text-muted-foreground"
                              onClick={() => clearOrganized(list.list_id)}
                            >
                              <X className="w-3 h-3" />
                              Vista original
                            </Button>
                          </div>

                          {/* Aisle sections */}
                          {(organizedDataMap[list.list_id].organized_items || []).map((aisle, aisleIdx) => {
                            const priceTier = selectedSupermarket?.price_tier;
                            return (
                              <div key={aisleIdx} className="rounded-lg border overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b">
                                  {aisle.aisle_number && (
                                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                                      {aisle.aisle_number}
                                    </span>
                                  )}
                                  <span className="text-base leading-none">{aisle.emoji}</span>
                                  <span className="font-semibold text-sm">{aisle.aisle}</span>
                                  <Badge variant="secondary" className="ml-auto text-xs px-1.5 py-0">
                                    {aisle.items.length}
                                  </Badge>
                                </div>
                                <Table>
                                  <TableBody>
                                    {aisle.items.map((item, iidx) => {
                                      const adjPrice = priceTier
                                        ? applyPriceMultiplier(item.estimated_cost, priceTier)
                                        : item.estimated_cost;
                                      const priceChanged = priceTier && priceTier !== 3;
                                      return (
                                        <TableRow key={`item-${aisle.aisle}-${iidx}`}>
                                          <TableCell className="font-medium py-2 text-sm">{item.ingredient_name}</TableCell>
                                          <TableCell className="text-right font-mono text-xs py-2 text-muted-foreground w-24">
                                            {item.quantity} {item.unit}
                                          </TableCell>
                                          <TableCell className="text-right py-2 w-28">
                                            <div className="flex flex-col items-end">
                                              <span className="font-mono text-sm">{formatCurrency(adjPrice)}</span>
                                              {priceChanged && (
                                                <span className="font-mono text-xs text-muted-foreground line-through">
                                                  {formatCurrency(item.estimated_cost)}
                                                </span>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            );
                          })}

                          {/* Footer */}
                          {(() => {
                            const priceTier = selectedSupermarket?.price_tier;
                            const baseTotal = list.total_estimated_cost ?? 0;
                            const allItems = (organizedDataMap[list.list_id]?.organized_items || []).flatMap(a => a.items);
                            const adjTotal = priceTier
                              ? allItems.reduce((s, item) => s + applyPriceMultiplier(item.estimated_cost ?? 0, priceTier), 0)
                              : baseTotal;
                            const delta = adjTotal - baseTotal;
                            return (
                              <div className="flex items-center justify-between pt-3 border-t">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    {(organizedDataMap[list.list_id]?.organized_items || []).length} pasillos
                                  </span>
                                  {priceTier && priceTier !== 3 && (
                                    <span className={`text-xs font-medium flex items-center gap-0.5 ${delta < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                                      {delta < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                      {delta < 0 ? "Ahorras" : "Pagas más"} {formatCurrency(Math.abs(delta))}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1 text-amber-700 hover:text-amber-700"
                                    onClick={() => startEditing(list)}
                                    data-testid={`edit-list-organized-btn-${list.list_id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                    Editar cantidades
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={() => handlePrint(list, true)}
                                    data-testid={`print-list-btn-${list.list_id}`}
                                  >
                                    <Printer className="w-3 h-3" />
                                    Imprimir con pasillos
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1 text-primary hover:text-primary"
                                    onClick={() => handlePreviewPdf(list.list_id)}
                                    disabled={downloadingPdf}
                                    data-testid={`preview-pdf-btn-${list.list_id}`}
                                  >
                                    {downloadingPdf
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <Eye className="w-3 h-3" />
                                    }
                                    Ver PDF
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1 text-primary hover:text-primary"
                                    onClick={() => handleDownloadPdf(list.list_id)}
                                    disabled={downloadingPdf}
                                    data-testid={`download-pdf-btn-${list.list_id}`}
                                  >
                                    {downloadingPdf
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <FileDown className="w-3 h-3" />
                                    }
                                    Descargar PDF
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={() => {
                                      if (emailInputListId === list.list_id) {
                                        setEmailInputListId(null);
                                        setEmailInputValue("");
                                      } else {
                                        setEmailInputListId(list.list_id);
                                        setEmailInputValue("");
                                      }
                                    }}
                                    data-testid={`send-email-organized-btn-${list.list_id}`}
                                  >
                                    <Mail className="w-3 h-3" />
                                    Enviar por email
                                  </Button>
                                  <div className="flex flex-col items-end">
                                    <span className="font-semibold text-lg text-primary">
                                      {formatCurrency(priceTier ? adjTotal : baseTotal)}
                                    </span>
                                    {priceTier && priceTier !== 3 && (
                                      <span className="text-xs text-muted-foreground line-through">
                                        {formatCurrency(baseTotal)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Inline email input — vista por pasillos */}
                          {emailInputListId === list.list_id && organizedDataMap[list.list_id] && (
                            <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap" data-testid={`email-input-row-organized-${list.list_id}`}>
                              <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                              <input
                                type="email"
                                placeholder="correo@ejemplo.com"
                                value={emailInputValue}
                                onChange={(e) => setEmailInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSendEmail(list.list_id);
                                  if (e.key === "Escape") { setEmailInputListId(null); setEmailInputValue(""); }
                                }}
                                className="flex-1 min-w-48 text-sm border border-border rounded px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                autoFocus
                                data-testid={`email-address-input-organized-${list.list_id}`}
                              />
                              <Button
                                size="sm"
                                className="h-8 text-xs gap-1 flex-shrink-0"
                                onClick={() => handleSendEmail(list.list_id)}
                                disabled={sendingEmail}
                                data-testid={`confirm-send-email-organized-btn-${list.list_id}`}
                              >
                                {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                Enviar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-xs flex-shrink-0"
                                onClick={() => { setEmailInputListId(null); setEmailInputValue(""); }}
                                disabled={sendingEmail}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* ── Normal / Edit flat list view ── */
                        <div>
                          {editingListId === list.list_id ? (
                            /* ── EDIT MODE ── */
                            <div className="space-y-3">
                              {/* Edit toolbar */}
                              <div className="flex items-center gap-2 flex-wrap p-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                                <Checkbox
                                  id={`select-all-${list.list_id}`}
                                  checked={editSelected.size === editItems.length && editItems.length > 0}
                                  onCheckedChange={handleSelectAllEdit}
                                  data-testid={`select-all-edit-${list.list_id}`}
                                />
                                <label htmlFor={`select-all-${list.list_id}`} className="text-xs font-medium cursor-pointer select-none">
                                  {editSelected.size === editItems.length && editItems.length > 0 ? "Deseleccionar todos" : "Seleccionar todos"}
                                </label>
                                {editSelected.size > 0 && (
                                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                    {editSelected.size} seleccionado{editSelected.size > 1 ? "s" : ""}
                                  </span>
                                )}
                                <div className="flex items-center gap-1 ml-auto">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    disabled={editSelected.size === 0}
                                    onClick={() => handleRound('up')}
                                    data-testid={`round-up-btn-${list.list_id}`}
                                    title="Redondear hacia arriba"
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                    Redondear &uarr;
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    disabled={editSelected.size === 0}
                                    onClick={() => handleRound('down')}
                                    data-testid={`round-down-btn-${list.list_id}`}
                                    title="Redondear hacia abajo"
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                    Redondear &darr;
                                  </Button>
                                </div>
                              </div>

                              {/* Editable table */}
                              <div className="overflow-x-auto rounded-lg border">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/50">
                                      <TableHead className="w-10"></TableHead>
                                      <TableHead>Ingrediente</TableHead>
                                      <TableHead className="text-right w-40">Cantidad</TableHead>
                                      <TableHead className="text-right w-28">Coste est.</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {editItems.map((item, idx) => (
                                      <TableRow
                                        key={item.ingredient_id || `temp-${idx}`}
                                        className={editSelected.has(idx) ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}
                                        data-testid={`edit-row-${idx}`}
                                      >
                                        <TableCell className="w-10">
                                          <Checkbox
                                            checked={editSelected.has(idx)}
                                            onCheckedChange={() => handleToggleEditItem(idx)}
                                            data-testid={`edit-item-check-${idx}`}
                                          />
                                        </TableCell>
                                        <TableCell className="font-medium text-sm">{item.ingredient_name}</TableCell>
                                        <TableCell className="text-right w-40">
                                          <div className="flex items-center justify-end gap-1.5">
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              value={item.quantity}
                                              onChange={(e) => handleQtyChange(idx, e.target.value)}
                                              className="w-20 text-right font-mono text-sm border border-border rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                              data-testid={`qty-input-${idx}`}
                                            />
                                            <span className="text-xs text-muted-foreground w-8 shrink-0">{item.unit}</span>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm w-28">
                                          {formatCurrency(item.estimated_cost)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>

                              {/* Edit footer */}
                              <div className="flex items-center justify-between pt-2 border-t">
                                <span className="text-sm text-muted-foreground">
                                  {editItems.length} productos
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-lg text-primary">
                                    {formatCurrency(editItems.reduce((s, i) => s + (i.estimated_cost || 0), 0))}
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={cancelEditing}
                                    disabled={savingEdit}
                                    data-testid={`cancel-edit-btn-${list.list_id}`}
                                  >
                                    <X className="w-3.5 h-3.5 mr-1" />
                                    Cancelar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs gap-1"
                                    onClick={() => saveEditing(list.list_id)}
                                    disabled={savingEdit}
                                    data-testid={`save-edit-btn-${list.list_id}`}
                                  >
                                    {savingEdit
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <Save className="w-3.5 h-3.5" />
                                    }
                                    Guardar cambios
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* ── NORMAL VIEW ── */
                            <div>
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Ingrediente</TableHead>
                                      <TableHead className="text-right">Cantidad</TableHead>
                                      <TableHead className="text-right">Coste est.</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {list.items.slice(0, 5).map((item, idx) => (
                                      <TableRow key={`row-${idx}`}>
                                        <TableCell className="font-medium">{item.ingredient_name}</TableCell>
                                        <TableCell className="text-right font-mono">
                                          {item.quantity} {item.unit}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                          {formatCurrency(item.estimated_cost)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    {list.items.length} productos
                                  </span>
                                  {list.items.length > 5 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-xs gap-1 text-primary"
                                      onClick={() => setViewingList(list)}
                                      data-testid={`view-list-btn-${list.list_id}`}
                                    >
                                      <Eye className="w-3 h-3" />
                                      Ver los {list.items.length} productos
                                    </Button>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={() => handlePrint(list, false)}
                                    data-testid={`print-list-btn-${list.list_id}`}
                                  >
                                    <Printer className="w-3 h-3" />
                                    Imprimir
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={() => {
                                      if (emailInputListId === list.list_id) {
                                        setEmailInputListId(null);
                                        setEmailInputValue("");
                                      } else {
                                        setEmailInputListId(list.list_id);
                                        setEmailInputValue("");
                                      }
                                    }}
                                    data-testid={`send-email-btn-${list.list_id}`}
                                  >
                                    <Mail className="w-3 h-3" />
                                    Enviar por email
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1 text-amber-700 hover:text-amber-700"
                                    onClick={() => startEditing(list)}
                                    data-testid={`edit-list-btn-${list.list_id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                    Editar cantidades
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1 text-primary"
                                    onClick={() => handleOrganizeByAisles(list)}
                                    disabled={organizingList === list.list_id}
                                    data-testid={`organize-aisles-btn-${list.list_id}`}
                                  >
                                    {organizingList === list.list_id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Sparkles className="w-3 h-3" />
                                    )}
                                    {organizingList === list.list_id ? "Organizando..." : "Organizar por pasillos"}
                                  </Button>
                                  <span className="font-semibold text-lg text-primary">
                                    {formatCurrency(list.total_estimated_cost)}
                                  </span>
                                </div>
                              </div>
                              {/* Inline email input */}
                              {emailInputListId === list.list_id && (
                                <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap" data-testid={`email-input-row-${list.list_id}`}>
                                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                                  <input
                                    type="email"
                                    placeholder="correo@ejemplo.com"
                                    value={emailInputValue}
                                    onChange={(e) => setEmailInputValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleSendEmail(list.list_id); if (e.key === "Escape") { setEmailInputListId(null); setEmailInputValue(""); } }}
                                    className="flex-1 min-w-48 text-sm border border-border rounded px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                    autoFocus
                                    data-testid={`email-address-input-${list.list_id}`}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs gap-1 flex-shrink-0"
                                    onClick={() => handleSendEmail(list.list_id)}
                                    disabled={sendingEmail}
                                    data-testid={`confirm-send-email-btn-${list.list_id}`}
                                  >
                                    {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    Enviar
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 text-xs flex-shrink-0"
                                    onClick={() => { setEmailInputListId(null); setEmailInputValue(""); }}
                                    disabled={sendingEmail}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Full list view dialog */}
      <ViewListDialog
        viewingList={viewingList}
        onClose={() => setViewingList(null)}
        onPrint={handlePrint}
      />

      {/* Convert to Order Dialog */}
      <ConvertToOrderDialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        selectedList={selectedList}
        suppliers={suppliers}
        selectedSupplier={selectedSupplier}
        setSelectedSupplier={setSelectedSupplier}
        onConvert={handleConvertToOrder}
        converting={converting}
      />

      {/* Receive + Update Stock Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={(open) => { if (!savingReceive) setReceiveDialogOpen(open); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Recibir pedido y actualizar inventario
            </DialogTitle>
            <DialogDescription>
              Revisa los productos de la lista. Asigna cada uno a un ingrediente existente o créalo como nuevo.
            </DialogDescription>
          </DialogHeader>

          {loadingReceive ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Buscando coincidencias en el inventario...</p>
            </div>
          ) : savingReceive ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground font-medium">{receiveProgress || "Guardando..."}</p>
              <p className="text-xs text-muted-foreground mt-2">Por favor espera</p>
            </div>
          ) : receivingList ? (
            <div className="space-y-4">
              {/* List info */}
              <div className="p-3 bg-muted rounded-lg text-sm flex flex-wrap gap-4">
                <span><strong>Lista:</strong> {receivingList.name}</span>
                <span><strong>Total estimado:</strong> {formatCurrency(receivingList.total_estimated_cost)}</span>
                <span><strong>Productos:</strong> {receivingList.items.length}</span>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  Coincidencia encontrada
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                  Crear nuevo ingrediente
                </span>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={receiveSelectedItems.length === receivingList.items.length}
                          onCheckedChange={(checked) =>
                            setReceiveSelectedItems(checked ? receivingList.items.map((_, i) => i) : [])
                          }
                        />
                      </TableHead>
                      <TableHead>Ingrediente</TableHead>
                      <TableHead>Cantidad</TableHead>
                      <TableHead>Coste est.</TableHead>
                      <TableHead className="min-w-[200px]">Asignar a ingrediente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivingList.items.map((item, index) => {
                      const mapping = receiveMappings[index] || { mode: 'new', ingredientId: null };
                      const isSelected = receiveSelectedItems.includes(index);
                      return (
                        <TableRow key={`recv-${item.ingredient_name}-${index}`} className={!isSelected ? "opacity-40" : ""}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                setReceiveSelectedItems(prev =>
                                  checked ? [...prev, index] : prev.filter(i => i !== index)
                                )
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">{item.ingredient_name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="font-mono text-sm">{formatCurrency(item.estimated_cost)}</TableCell>
                          <TableCell>
                            <Select
                              value={mapping.mode === 'existing' ? mapping.ingredientId : "new"}
                              onValueChange={(val) =>
                                setReceiveMappings(prev => ({
                                  ...prev,
                                  [index]: val === "new"
                                    ? { mode: 'new', ingredientId: null }
                                    : { mode: 'existing', ingredientId: val }
                                }))
                              }
                            >
                              <SelectTrigger className={`h-8 text-xs ${mapping.mode === 'existing' ? "border-green-400 bg-green-50 dark:bg-green-900/20" : "border-blue-300 bg-blue-50 dark:bg-blue-900/20"}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">
                                  <span className="flex items-center gap-1.5">
                                    <Plus className="w-3 h-3 text-blue-500" />
                                    Crear nuevo
                                  </span>
                                </SelectItem>
                                {receiveIngredients.length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-muted-foreground font-medium border-t mt-1 pt-2">
                                      Ingredientes existentes
                                    </div>
                                    {receiveIngredients.map(ing => (
                                      <SelectItem key={ing.ingredient_id} value={ing.ingredient_id}>
                                        {ing.name}
                                        <span className="text-muted-foreground ml-1 text-xs">
                                          (stock: {ing.current_stock} {ing.unit})
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              {receiveSelectedItems.length > 0 && (
                <div className="text-sm text-muted-foreground flex gap-4 pt-1">
                  <span>
                    <strong className="text-green-600">
                      {receiveSelectedItems.filter(i => receiveMappings[i]?.mode === 'existing').length}
                    </strong> se actualizarán
                  </span>
                  <span>
                    <strong className="text-blue-600">
                      {receiveSelectedItems.filter(i => receiveMappings[i]?.mode !== 'existing').length}
                    </strong> se crearán nuevos (con merma calculada por IA)
                  </span>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)} disabled={savingReceive}>
              Cancelar
            </Button>
            <Button
              onClick={handleReceiveConfirm}
              disabled={savingReceive || loadingReceive || receiveSelectedItems.length === 0}
              data-testid="confirm-receive-btn"
            >
              {savingReceive
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <Check className="w-4 h-4 mr-2" />
              }
              Confirmar recepción ({receiveSelectedItems.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Receive with Invoice Dialog ── */}
      <ReceiveInvoiceDialog
        open={receiveInvoiceDialogOpen}
        onOpenChange={setReceiveInvoiceDialogOpen}
        loading={receiveInvoiceLoading}
        confirming={confirmingInvoice}
        progress={invoiceProgress}
        comparison={invoiceComparison}
        invoiceFile={invoiceFile}
        invoicePreview={invoicePreview}
        onFileSelect={handleInvoiceFileSelect}
        onConfirm={confirmInvoiceReceive}
      />
    </div>
  );
}
